import { motion } from 'framer-motion';
import { LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AnimatedCounter } from '@/components/ui/animated-counter';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface AnalyticsMetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: number;
  subtitle?: string;
  delay?: number;
}

export function AnalyticsMetricCard({
  title,
  value,
  icon: Icon,
  trend,
  subtitle,
  delay = 0,
}: AnalyticsMetricCardProps) {
  const prefersReducedMotion = useReducedMotion();
  const TrendIcon = trend === undefined ? null : trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus;
  const numericValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
  const isNumeric = !isNaN(numericValue);
  
  return (
    <motion.div
      initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay * 0.1 }}
      whileHover={prefersReducedMotion ? {} : { 
        y: -4, 
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      }}
      className="surface-elevated rounded-xl p-5 transition-shadow"
    >
      <div className="flex items-center justify-between mb-3">
        <motion.div 
          className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"
          whileHover={prefersReducedMotion ? {} : { scale: 1.1, rotate: 5 }}
          transition={{ type: 'spring', stiffness: 400 }}
        >
          <Icon className="h-5 w-5 text-primary" />
        </motion.div>
        {trend !== undefined && TrendIcon && (
          <motion.div 
            initial={prefersReducedMotion ? {} : { scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: delay * 0.1 + 0.2, type: 'spring' }}
            className={cn(
              "flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-full",
              trend > 0 && "bg-primary/10 text-primary",
              trend < 0 && "bg-destructive/10 text-destructive",
              trend === 0 && "bg-muted text-muted-foreground"
            )}
          >
            <TrendIcon className="h-3.5 w-3.5" />
            <span>{Math.abs(trend)}%</span>
          </motion.div>
        )}
      </div>
      <div className="text-3xl font-bold mb-1">
        {isNumeric ? (
          <AnimatedCounter 
            value={numericValue} 
            formatFn={(v) => typeof value === 'string' && value.includes('%') 
              ? `${Math.round(v)}%` 
              : Math.round(v).toLocaleString()
            }
          />
        ) : (
          value
        )}
      </div>
      <div className="text-sm text-muted-foreground">{title}</div>
      {subtitle && (
        <div className="text-xs text-muted-foreground/70 mt-1">{subtitle}</div>
      )}
    </motion.div>
  );
}
